import { ChangeEvent, useEffect, useState } from "react"
import { IOmniaShell } from "@omnia/types"
import { IDashboardService } from "../types"
import { FieldViewModel, str } from "../omnia"
import { Card } from "@omnia/shared/react/Card"
import { useAsyncValue } from "@omnia/shared/react/hooks"
import { CroppingYearChartComponent, CroppingYearData } from "./CroppingYearChartComponent"


const ALL_FIELD_GROUPS = "All field groups";
const NO_FIELD_GROUP = "(no field group)";

/** Calculates the total area for the given set of fields. */
function getTotalArea(fields: FieldViewModel[]): number {
    return Math.round(fields.map(c => c.cropping.Area).reduce((a1, a2) => a1 + a2) * 100) / 100;
};

/** For the given set of fileds (cropping), returns data for the pie chart. */
function getFieldsPieData(cropping?: FieldViewModel[]): CroppingYearData {
    if ((cropping?.length ?? 0) === 0) {
        return undefined;
    }

    var groups = omnia.groupBy(cropping, c => c.cropping.Crop, c => c.CropId)
        .sort((a, b) => getTotalArea(a.values) > getTotalArea(b.values) ? -1 : getTotalArea(a.values) < getTotalArea(b.values) ? 1 : 0);
    var data = [];
    var colours = [];
    var labels = [];

    for (var i = 0; i < groups.length; i++) {
        var group = groups[i];
        var colour = omnia.fields.getCropCssColor(group.key);
        data.push(Math.round(group.values.map(c => c.cropping.Area).reduce((a1, a2) => a1 + a2) * 100) / 100);
        colours.push(colour);
        labels.push(group.key.Name ? group.key.Name : str.Uncropped);
    }

    return {
        labels: labels,
        datasets: [{
            data: data,
            backgroundColor: colours,
            label: str.Fields
        }]
    };
}

interface Props {
    svc: IDashboardService
    shell: IOmniaShell
    update(run?: () => void): void
}

export function CropsCard({svc, shell, update}: Props) {
    const [fieldGroups, setFieldGroups] = useState<string[]>([]);
    const [selectedFieldGroup, setSelectedFieldGroup] = useState<string>();
    const [dataCurrent, setDataCurrent] = useState<CroppingYearData>();
    const [dataPrevious, setDataPrevious] = useState<CroppingYearData>();

    // wait for the init, calculate all the data and assign to yearInfo
    const yearInfo = useAsyncValue(() => svc.ensureInit().then(() => {
        const currentYear = svc.getFieldService().yearId;

        return { 
            currentYear: currentYear, 
            previousYear: currentYear - 1,
            croppingData: svc.getFieldService().cropping,
        };
    }), [svc, svc.yearId, svc.stateVersion]);
    
    // if the cropping data changes, update the dropdown fieldGroups
    useEffect(() => {
        const cping = yearInfo?.croppingData;

        if ((cping?.length ?? 0) === 0) {
            setFieldGroups([ALL_FIELD_GROUPS]);
            setSelectedFieldGroup(prev => ALL_FIELD_GROUPS);
            return;
        }

        // extract the group names (for any field which does not have a group, use the 'no field group')
        const groups = cping?.map(f => !!f.fieldGroup ? f.fieldGroup : NO_FIELD_GROUP) ?? [];
        const uniqueGroups = [...new Set(groups)].sort((a, b) => a.localeCompare(b));

        // set the new groups, adding the "All" option on top
        setFieldGroups([ALL_FIELD_GROUPS, ...uniqueGroups]);
        setSelectedFieldGroup(prev => prev ?? ALL_FIELD_GROUPS);
    }, [yearInfo?.croppingData]);

    // if the year data or the field group selection changes, update the pie charts
    useEffect(() => {
        const isSelected = (c: FieldViewModel) => (
            // all fields should be returned
            selectedFieldGroup === ALL_FIELD_GROUPS 
            // or just the selected fields
            || c.fieldGroup === selectedFieldGroup 
            // or just the fields with no field group set
            || (selectedFieldGroup === NO_FIELD_GROUP && !Boolean(c.fieldGroup)));

        const filteredCurrent = yearInfo?.croppingData?.filter(c => c.currentYear && isSelected(c));
        const filteredPrevious = yearInfo?.croppingData?.filter(c => c.cropping.YearId === yearInfo?.previousYear && isSelected(c))

        setDataCurrent(getFieldsPieData(filteredCurrent));
        setDataPrevious(getFieldsPieData(filteredPrevious));
    }, [yearInfo?.croppingData, yearInfo?.previousYear, selectedFieldGroup]);

    const handleGroupChange = (e: ChangeEvent<HTMLSelectElement>) => setSelectedFieldGroup(e.target.value);

    const cardHeaderContent = <>
        {/* if length > 2 it means that we have more than 1 field group to select from and we should show the dropdown */}
        {fieldGroups?.length > 2 && <div className="form-inline"><div className="form-group">
                <select id="groupDropdown" className="form-control" value={selectedFieldGroup ?? ALL_FIELD_GROUPS} onChange={handleGroupChange}>
                    {fieldGroups.map(group => (
                        <option key={group} value={group}>{group}</option>
                    ))}
                </select>
            </div></div>
        }
    </>

    const cardTools = <>
        <div className="btn-group">
            <button className="btn btn-link btn-icon-toggle" onClick={() => update(() => shell.navigate("/fields"))} title="Manage fields & croppings">
                <i className="fas fa-pen"></i>
            </button>
        </div>
    </>

    return <>
        <Card 
            icon="icon icon-fields"
            title="Fields Summary" 
            small={false} 
            headerContent={cardHeaderContent}
            headerContentAlign="right"
            headerTools={cardTools}
            isCollapsible={true}>

            <div className="row">
                <div className="col-lg-6">
                    <CroppingYearChartComponent 
                        title={`Previous cropping year (${yearInfo?.previousYear})`}
                        yearData={dataPrevious} />
                </div>
                <div className="col-lg-6">
                    <CroppingYearChartComponent 
                        title={`Current cropping year (${yearInfo?.currentYear})`}
                        yearData={dataCurrent} />
                </div>
            </div>

        </Card>
    </>
}